import { ExternalLink, Plus, Send } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  useCreateSurvey,
  useSendSurveyReminder,
  useTrainingSurvey,
} from '@/hooks/use-training-surveys'
import { useTrainings } from '@/hooks/use-trainings'

/** 研修一覧にはアンケートが含まれないため、研修ごとに取得して表示する */
function TrainingSurveyCard({
  trainingId,
  title,
  onRemind,
  isReminding,
}: {
  trainingId: string
  title: string
  onRemind: (surveyId: string) => void
  isReminding: boolean
}) {
  const { data, isLoading } = useTrainingSurvey(trainingId)
  const survey = data as { id: string; formsUrl: string } | null | undefined

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {isLoading ? (
          <Skeleton className="h-8 w-40" />
        ) : survey ? (
          <div className="space-y-2">
            <a
              href={survey.formsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-emerald-600 hover:underline"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Forms URL
            </a>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onRemind(survey.id)}
              disabled={isReminding}
            >
              <Send className="mr-1 h-3.5 w-3.5" />
              リマインダー送信
            </Button>
          </div>
        ) : (
          <Badge variant="outline">アンケート未設定</Badge>
        )}
      </CardContent>
    </Card>
  )
}

export default function TrainingSurveyManagePage() {
  const { data, isLoading } = useTrainings()
  const createSurvey = useCreateSurvey()
  const sendReminder = useSendSurveyReminder()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedTrainingId, setSelectedTrainingId] = useState('')
  const [formsUrl, setFormsUrl] = useState('')

  const trainings = Array.isArray(data) ? data : ((data as any)?.data ?? [])

  const handleCreateSurvey = async () => {
    if (!selectedTrainingId || !formsUrl) return
    try {
      await createSurvey.mutateAsync({ trainingId: selectedTrainingId, formsUrl })
      toast.success('アンケートを作成しました')
      setDialogOpen(false)
      setFormsUrl('')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '作成に失敗しました')
    }
  }

  const handleReminder = async (surveyId: string) => {
    try {
      await sendReminder.mutateAsync(surveyId)
      toast.success('リマインダーの送信を受け付けました')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '送信に失敗しました')
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">アンケート管理</h1>
        <Skeleton className="h-64" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">アンケート管理</h1>
        <Button
          onClick={() => {
            setSelectedTrainingId('')
            setFormsUrl('')
            setDialogOpen(true)
          }}
        >
          <Plus className="mr-1 h-4 w-4" />
          アンケート作成
        </Button>
      </div>

      {trainings.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-muted-foreground">研修が登録されていません</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {trainings.map((t: any) => (
            <TrainingSurveyCard
              key={t.id}
              trainingId={t.id}
              title={t.title}
              onRemind={handleReminder}
              isReminding={sendReminder.isPending}
            />
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>アンケート作成</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>研修</Label>
              <select
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={selectedTrainingId}
                onChange={(e) => setSelectedTrainingId(e.target.value)}
              >
                <option value="">選択してください</option>
                {trainings.map((t: any) => (
                  <option key={t.id} value={t.id}>
                    {t.title}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Microsoft Forms URL</Label>
              <Input
                type="url"
                placeholder="https://forms.office.com/..."
                value={formsUrl}
                onChange={(e) => setFormsUrl(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              キャンセル
            </Button>
            <Button
              onClick={handleCreateSurvey}
              disabled={!selectedTrainingId || !formsUrl || createSurvey.isPending}
            >
              {createSurvey.isPending ? '作成中...' : '作成'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
